import { FontConfig, TextOnPathConfig, TextOnPathLayer } from 'smg/customizer';

const DEFAULT_FONT_SIZE_PT = 6;

// Conversion: 1024px canvas / 1.25" pin = ~819 DPI
const PIN_DPI = 1024 / 1.25;

/** Convert pt to px for rendering on pin canvas */
function ptToPx(pt: number): number {
    return Math.round(pt * (PIN_DPI / 72));
}

/** Convert px to pt for returning font sizes */
function pxToPt(px: number): number {
    return Math.round(px / (PIN_DPI / 72));
}

// Shift text up by 15% of font size to make room for underline
const UNDERLINE_BASELINE_OFFSET = -0.15;

/** Signature-specific text layer for ribbon pins — handles pt↔px conversion for font sizes. */
export class RibbonTextLayer extends TextOnPathLayer {
    #fontSizePt: number;
    #hasUnderline: boolean = false;

    constructor(id: string, config: TextOnPathConfig) {
        // Convert pt→px for parent class, which expects px
        const sizePt = config.font.size ?? DEFAULT_FONT_SIZE_PT;
        const configWithPx = {
            ...config,
            font: { ...config.font, size: ptToPx(sizePt) },
        };
        super(id, configWithPx);
        this.#fontSizePt = sizePt;
    }

    override async setFont(font: Partial<FontConfig>): Promise<void> {
        // Track underline state changes
        const newUnderline = font.textDecoration === 'underline';
        const underlineChanged = font.textDecoration !== undefined && newUnderline !== this.#hasUnderline;

        // Update stored pt size if provided
        if (font.size !== undefined) {
            this.#fontSizePt = font.size;
        }

        // Apply underline adjustments
        if (underlineChanged) {
            this.#hasUnderline = newUnderline;
        }

        // Build font config with px conversion and baseline offset
        const adjustedFont: Partial<FontConfig> = {
            ...font,
            size: ptToPx(this.#fontSizePt),
            baselineOffset: this.#hasUnderline ? UNDERLINE_BASELINE_OFFSET : undefined,
        };

        await super.setFont(adjustedFont);
    }

    override getFont(): FontConfig {
        const font = super.getFont();
        // Return pt value, not px
        return { ...font, size: this.#fontSizePt };
    }

    /** Rendered font size in canvas px (getFont() reports pt for the page UI). */
    getFontSizePx(): number {
        return this.config.font.size ?? ptToPx(DEFAULT_FONT_SIZE_PT);
    }

    setPathData(pathData: string): void {
        this.config.pathData = pathData;
        // Update the path element directly (this.path is protected in parent)
        this.path.setAttribute('d', pathData);
        void this.updateCanvas();
    }

    /**
     * Shrink font to fit within a perpendicular distance band around the path.
     * Returns font size in pt (unlike parent which uses px).
     * @param maxDistancePx - Maximum perpendicular distance from path in pixels
     * @param minFontSizePt - Minimum font size in pt (default: 1)
     * @returns Font size in pt and whether shrinking occurred
     */
    async shrinkToFitPathPtAsync(maxDistancePx: number, minFontSizePt: number = 1): Promise<{ fontSizePt: number; didShrink: boolean } | null> {
        const minFontSizePx = ptToPx(minFontSizePt);
        const result = await super.shrinkToFitPathAsync(maxDistancePx, minFontSizePx);
        if (!result) {
            return null;
        }
        return {
            fontSizePt: pxToPt(result.fontSize),
            didShrink: result.didShrink,
        };
    }

    /**
     * Shrink font until text length fits within path length (no characters clipped at path ends).
     * Uses binary search. Returns font size in pt.
     * @param minFontSizePt - Minimum font size in pt (default: 1)
     * @returns Font size in pt and whether shrinking occurred
     */
    async shrinkToFitPathLengthPtAsync(minFontSizePt: number = 1): Promise<{ fontSizePt: number; didShrink: boolean } | null> {
        if (!this.getText()) {
            return null;
        }

        const startingSizePt = this.#fontSizePt;
        const currentPercent = await this.getTextLengthPercentAsync();

        if (currentPercent <= 100) {
            return { fontSizePt: startingSizePt, didShrink: false };
        }

        let low = minFontSizePt;
        let high = startingSizePt;
        let bestFit = minFontSizePt;

        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            await super.setFont({ size: ptToPx(mid) });
            const percent = await this.getTextLengthPercentAsync();

            if (percent <= 100) {
                bestFit = mid;
                low = mid + 1;
            } else {
                high = mid - 1;
            }
        }

        this.#fontSizePt = bestFit;
        await super.setFont({ size: ptToPx(bestFit) });

        return { fontSizePt: bestFit, didShrink: bestFit < startingSizePt };
    }

    /**
     * Shrink this layer's font until it no longer intersects with another layer.
     * Returns font size in pt.
     * @param other - The other RibbonTextLayer to avoid intersecting
     * @param minFontSizePt - Minimum font size in pt (default: 1)
     * @returns Font size in pt and whether shrinking occurred
     */
    async shrinkToAvoidIntersectionPtAsync(other: RibbonTextLayer, minFontSizePt: number = 1): Promise<{ fontSizePt: number; didShrink: boolean } | null> {
        const minFontSizePx = ptToPx(minFontSizePt);
        const result = await super.shrinkToAvoidIntersection(other, minFontSizePx);
        if (!result) {
            return null;
        }
        return {
            fontSizePt: pxToPt(result.fontSize),
            didShrink: result.didShrink,
        };
    }
}
